import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {Fragment, memo, useMemo} from 'react'

import {GroupHeader} from '../../../components/common/group/group-header'
import {GroupHeaderLabel} from '../../../components/common/group/label/group-header-label'
import {ReorderableRowsContext} from '../../../components/table/row-reordering/reorderable-rows'
import type {TableDataType} from '../../../components/table/table-data-type'
import {TableGroupSeparator} from '../../../components/table/table-group-separator'
import type {ItemDataType} from '../../../components/table/use-item-data'
import type {GroupingMetadataWithSource} from '../../../features/grouping/types'
import {resolveRawTitleForGroupingContext} from '../../../helpers/table-group-utilities'
import {ViewerPrivileges} from '../../../helpers/viewer-privileges'
import type {Row} from '../../../hooks/tanstack-table/helpers/types'
import {useAggregationSettings} from '../../../hooks/use-aggregation-settings'
import {useDroppableGroup} from '../../../hooks/use-droppable-group'
import {useRoadmapTableWidth} from '../../../hooks/use-roadmap-settings'
import {pageTypeForGroups} from '../../../state-providers/memex-items/queries/query-keys'
import {RoadmapGroupPillArea} from './roadmap-group-pill-area'
import styles from './roadmap-grouped-items.module.css'
import {RoadmapItems} from './roadmap-items'
import {RoadmapPagination} from './roadmap-pagination'
import sharedStyles from './roadmap-shared.module.css'
import {RoadmapTableColumnResizeProvider} from './roadmap-table-column-resize-provider'
import {RoadmapTableDragSash} from './roadmap-table-drag-sash'
/**
 * Renders Roadmap item rows grouped by a specific column.
 */
export function GroupedRoadmapItems({
  collapsedGroupIds,
  tableGroups,
  itemData,
}: {
  collapsedGroupIds: Array<string>
  tableGroups: Array<Row<TableDataType>>
  itemData: ItemDataType
}) {
  return (
    <Fragment>
      {tableGroups.map((group, index) => {
        const collapsed = collapsedGroupIds.includes(group.groupedValue)
        return (
          <Fragment key={group.id}>
            <RoadmapGroup
              metadata={{
                value: group.groupedValue,
                sourceObject: group.groupedSourceObject,
              }}
              totalCount={group.totalCount}
              index={index}
              collapsed={collapsed}
              rows={group.subRows}
              itemData={itemData}
              groupId={group.id}
            />
            <TableGroupSeparator />
          </Fragment>
        )
      })}
      <RoadmapPagination pageType={pageTypeForGroups} />
    </Fragment>
  )
}

/**
 * Renders a group of Roadmap items.
 */
const RoadmapGroup = memo(function RoadmapGroup({
  collapsed,
  metadata,
  rows,
  itemData,
  groupId,
  totalCount,
}: {
  collapsed: boolean
  metadata: GroupingMetadataWithSource
  index: number
  rows: Array<Row<TableDataType>>
  itemData: ItemDataType
  groupId: string
  totalCount: number
}) {
  const {hideItemsCount, getAggregatesForGroupId} = useAggregationSettings()
  const aggregates = useMemo(() => getAggregatesForGroupId(metadata.value), [getAggregatesForGroupId, metadata.value])
  const title = resolveRawTitleForGroupingContext(metadata.sourceObject)
  const {hasWritePermissions} = ViewerPrivileges()

  const {setNodeRef, isOver} = useDroppableGroup({
    groupId,
    groupedValue: metadata.value,
    isCollapsed: collapsed,
    isEmpty: rows.length === 0,
  })
  const tableWidth = useRoadmapTableWidth()

  return (
    <div
      {...testIdProps(`roadmap-group-${title}`)}
      role="rowgroup"
      className={clsx(sharedStyles.RowGroup, styles.RowGroup, collapsed && styles.isCollapsed, isOver && styles.isOver)}
      ref={setNodeRef}
    >
      <RoadmapGroupHeaderRow>
        <GroupHeader
          isCollapsed={collapsed}
          metadata={metadata}
          itemsInGroup={rows.map(row => row.original)}
          style={{
            zIndex: 'var(--roadmap-cell-z-index)',
            minWidth: tableWidth,
            width: tableWidth,
          }}
          className={clsx(styles.roadmapGroupHeader, styles.GroupHeader)}
        >
          <GroupHeaderLabel
            sourceObject={metadata.sourceObject}
            aggregates={aggregates}
            hideItemsCount={hideItemsCount}
            rowCount={totalCount}
            titleClassName={styles.GroupHeaderLabel}
          />
          {hasWritePermissions && (
            <RoadmapTableColumnResizeProvider>
              <RoadmapTableDragSash id={`RoadmapTableDragSash-${metadata.value}`} />
            </RoadmapTableColumnResizeProvider>
          )}
        </GroupHeader>
        <RoadmapGroupPillArea rows={rows} />
      </RoadmapGroupHeaderRow>
      {!collapsed && (
        <ReorderableRowsContext rows={rows} groupedValue={metadata.value}>
          <RoadmapItems
            groupMetadata={metadata}
            rows={rows}
            isOmnibarFixed={false}
            itemData={itemData}
            groupId={groupId}
          />
        </ReorderableRowsContext>
      )}
    </div>
  )
})

/**
 * Renders the group header row comprised of the table cell and summary pill area.
 */
const RoadmapGroupHeaderRow = ({children}: {children: React.ReactNode}) => {
  return (
    <div role="row" className={styles.Box} {...testIdProps('roadmap-group-header-row')}>
      {children}
    </div>
  )
}

try{ GroupedRoadmapItems.displayName ||= 'GroupedRoadmapItems' } catch {}
try{ RoadmapGroup.displayName ||= 'RoadmapGroup' } catch {}
try{ RoadmapGroupHeaderRow.displayName ||= 'RoadmapGroupHeaderRow' } catch {}